import { sanitizeHtml } from '../../utils/safeHtml';
import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Eye, FileText, ChevronDown, Bold, Italic, List, ListOrdered, Heading1, Heading2, Link as LinkIcon, History, Download, X } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

export default function LegalTextsAdmin() {
  const [legalTexts, setLegalTexts] = useState([]);
  const [selectedPage, setSelectedPage] = useState('impressum');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Versions-Historie
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  
  // Editor state
  const [title, setTitle] = useState('');
  const [initialContent, setInitialContent] = useState('');
  
  // Integrity Metadaten (read-only)
  const [metadata, setMetadata] = useState({
    version_number: null,
    updated_at: null,
    updated_by: null,
    content_hash: null,
    timestamp_provider: null,
    timestamp_proof: null,
    timestamped_at: null,
    timestamp_status: null
  });
  
  const pages = [
    { key: 'impressum', label: 'Impressum' },
    { key: 'datenschutz', label: 'Datenschutz' },
    { key: 'agb', label: 'AGB' },
    { key: 'kontakt', label: 'Kontakt' }
  ];
  
  // Tiptap Editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none p-4 min-h-[500px] focus:outline-none text-black',
      },
    },
  });
  
  // Update editor content when page changes
  useEffect(() => {
    if (editor && initialContent) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);
  
  useEffect(() => {
    loadAllTexts();
  }, []);
  
  useEffect(() => {
    if (selectedPage && legalTexts.length > 0) {
      loadSelectedPage();
    }
  }, [selectedPage, legalTexts]);
  
  const loadAllTexts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/legal-texts/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Texte');
      }
      
      const data = await response.json();
      setLegalTexts(data.legal_texts || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading texts:', err);
      setError('Fehler beim Laden der Texte');
      setLoading(false);
    }
  };
  
  const loadSelectedPage = () => {
    const text = legalTexts.find(t => t.page_key === selectedPage);
    if (text) {
      setTitle(text.title || '');
      setInitialContent(text.content || '');
      
      // Lade Metadaten
      setMetadata({
        version_number: text.version_number,
        updated_at: text.updated_at,
        updated_by: text.updated_by,
        content_hash: text.content_hash,
        timestamp_provider: text.timestamp_provider,
        timestamp_proof: text.timestamp_proof,
        timestamped_at: text.timestamped_at,
        timestamp_status: text.timestamp_status
      });
    }
  };
  
  const loadVersions = async () => {
    try {
      setLoadingVersions(true);
      const token = localStorage.getItem('snapshot_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/legal-texts/admin/${selectedPage}/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Versionen');
      }
      
      const data = await response.json();
      setVersions(data.versions || []);
      setLoadingVersions(false);
      setShowVersions(true);
    } catch (err) {
      console.error('Error loading versions:', err);
      setError('Fehler beim Laden der Versionen');
      setLoadingVersions(false);
    }
  };
  
  const downloadOtsProof = (proof, pageKey, hash) => {
    if (!proof) return;
    
    try {
      const hexProof = proof;
      const bytes = new Uint8Array(hexProof.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
      const blob = new Blob([bytes], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pageKey}_${hash?.substring(0, 16)}.ots`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError('Fehler beim Download');
    }
  };

  
  const saveText = async () => {
    if (!editor) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      const token = localStorage.getItem('snapshot_token');
      
      // Hole sauberes HTML vom Editor
      const htmlContent = editor.getHTML();
      
      const payload = {
        title,
        content: htmlContent
      };
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/legal-texts/admin/${selectedPage}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Fehler beim Speichern');
      }
      
      setSuccess('✅ Gespeichert! Änderungen sind sofort live.');
      await loadAllTexts();
      setSaving(false);
      
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error saving:', err);
      setError('❌ Fehler beim Speichern');
      setSaving(false);
    }
  };
  
  const setLink = () => {
    if (!editor) return;
    const url = window.prompt('URL eingeben:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#10B981]/30 border-t-[#10B981]"></div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black grain-texture text-white p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileText className="w-8 h-8 text-[#10B981]" />
            <h1 className="text-3xl font-bold">Rechtstexte verwalten</h1>
          </div>
          <p className="text-white/60">
            Impressum, Datenschutz, AGB und Kontakt bearbeiten – wie in Word
          </p>
        </div>
        
        {/* Seitenauswahl */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/80 mb-2">
            Seite auswählen
          </label>
          <div className="relative">
            <select
              value={selectedPage}
              onChange={(e) => setSelectedPage(e.target.value)}
              className="w-full md:w-64 px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white appearance-none cursor-pointer hover:bg-white/10 transition-all"
            >
              {pages.map(page => (
                <option key={page.key} value={page.key} className="bg-black">
                  {page.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
          </div>
        </div>
        
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-500">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg flex items-center gap-3">
            <Eye className="w-5 h-5 text-[#10B981]" />
            <span className="text-[#10B981]">{success}</span>
          </div>
        )}
        
        {/* Editor & Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* LEFT: WYSIWYG Editor */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#10B981]">Editor</h2>
            </div>
            
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">
                Titel
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:border-[#10B981]"
                placeholder="z.B. Impressum"
              />
            </div>
            
            {/* Toolbar */}
            {editor && (
              <div className="flex flex-wrap gap-1 p-2 bg-white/10 border border-white/20 rounded-lg">
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-2 rounded ${editor.isActive('heading', { level: 2 }) ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Überschrift 2"
                >
                  <Heading2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-2 rounded text-sm font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Überschrift 3"
                >
                  H3
                </button>
                <div className="w-px h-8 bg-white/20 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`p-2 rounded ${editor.isActive('bold') ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Fett"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded ${editor.isActive('italic') ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Kursiv"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <div className="w-px h-8 bg-white/20 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded ${editor.isActive('bulletList') ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Aufzählung"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded ${editor.isActive('orderedList') ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Nummerierte Liste"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
                <div className="w-px h-8 bg-white/20 mx-1"></div>
                <button
                  onClick={setLink}
                  className={`p-2 rounded ${editor.isActive('link') ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}
                  title="Link einfügen"
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
              </div>
            )}
            
            {/* Rich-Text Editor */}
            <div className="bg-white border border-white/20 rounded-lg overflow-hidden" style={{ minHeight: '500px' }}>
              <EditorContent editor={editor} />
            </div>
            <p className="text-white/40 text-xs">
              Formatierung über die Toolbar – wie in Word
            </p>
            
            {/* Save Button */}
            <button
              onClick={saveText}
              disabled={saving || !editor}
              className={`w-full py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                saving || !editor
                  ? 'bg-white/10 text-white/40 cursor-not-allowed'
                  : 'bg-[#10B981] text-black hover:bg-[#059669] hover:shadow-xl'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                  Speichert...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Speichern & Live schalten
                </>
              )}
            </button>
            
            {/* Integrity Metadaten (read-only) */}
            {metadata.content_hash && (
              <div className="mt-6 p-4 bg-white/5 border border-[#10B981]/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-[#10B981] rounded-full"></div>
                  <h3 className="text-sm font-bold text-[#10B981]">Dokumenten-Integrität</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-white/50 mb-1">Letzte Änderung</p>
                    <p className="text-white font-mono">
                      {metadata.updated_at ? new Date(metadata.updated_at).toLocaleString('de-DE', { timeZone: 'UTC', hour12: false }) + ' UTC' : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-white/50 mb-1">Geändert von</p>
                    <p className="text-white font-mono truncate" title={typeof metadata.updated_by === 'object' ? metadata.updated_by?.email : metadata.updated_by}>
                      {typeof metadata.updated_by === 'object' ? metadata.updated_by?.email : metadata.updated_by || 'N/A'}
                    </p>
                  </div>
                  
                  <div className="col-span-2">
                    <p className="text-white/50 mb-1">Dokumenten-Hash (SHA-256)</p>
                    <p className="text-[#10B981] font-mono text-xs break-all">
                      {metadata.content_hash ? `${metadata.content_hash.substring(0, 32)}...` : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-white/50 mb-1">Externer Zeitstempel</p>
                    <p className="text-white">
                      {metadata.timestamp_provider === 'OpenTimestamps' ? (
                        <span className="text-[#10B981]">✓ {metadata.timestamp_provider}</span>
                      ) : (
                        <span className="text-white/40">Nicht verfügbar</span>
                      )}
                    </p>
                  </div>
                  
                  {metadata.timestamp_proof && (
                    <div className="col-span-2">
                      <p className="text-white/50 mb-1">Zeitstempel-Proof (.ots)</p>
                      <div className="flex items-center gap-2">
                        <p className="text-white/70 font-mono text-xs truncate flex-1" title={metadata.timestamp_proof}>
                          {metadata.timestamp_proof.substring(0, 32)}...
                        </p>
                        <button
                          onClick={() => {
                            // Download .ots Proof
                            const hexProof = metadata.timestamp_proof;
                            const bytes = new Uint8Array(hexProof.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
                            const blob = new Blob([bytes], { type: 'application/octet-stream' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${selectedPage}_${metadata.content_hash?.substring(0, 16)}.ots`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className="px-3 py-1 bg-[#10B981] text-black text-xs rounded hover:bg-[#059669] transition-all"
                        >
                          Download .ots
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                <p className="text-white/40 text-xs pt-2 border-t border-white/10">
                  🔒 Diese Felder sind unveränderbar und dienen als Integritätsnachweis
                </p>
              </div>
            )}
          </div>
          
          {/* RIGHT: Live-Vorschau */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#10B981]">Live-Vorschau</h2>
              <Eye className="w-5 h-5 text-white/40" />
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-lg p-6 min-h-[600px] max-h-[700px] overflow-y-auto">
              <div className="prose prose-invert max-w-none">
                <h1 className="text-3xl font-bold mb-6 text-white">{title || 'Titel...'}</h1>
                <div 
                  className="text-white/90 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(editor ? editor.getHTML() : '<p class="text-white/40">Kein Inhalt...</p>') }}
                />
              </div>
            </div>
            
            <div className="text-xs text-white/40">
              ℹ️ Diese Vorschau zeigt, wie der Text auf der Website aussieht
            </div>
          </div>
          
        </div>
        
        {/* Info Box */}
        <div className="mt-8 p-4 bg-[#10B981]/5 border border-[#10B981]/20 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-white/80 flex-1">
              <p className="font-semibold text-[#10B981] mb-1">WYSIWYG-Editor</p>
              <p>
                Schreibe normalen Text wie in Word. Formatierung über die Toolbar-Buttons. 
                Änderungen sind nach dem Speichern sofort auf der Live-Seite sichtbar.
              </p>
            </div>
            <button
              onClick={loadVersions}
              disabled={loadingVersions}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white text-sm font-medium transition-all flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              Versions-Historie
            </button>
          </div>
        </div>
        
        {/* Versions-Historie Modal */}
        {showVersions && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="bg-black border-2 border-[#10B981]/30 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              
              {/* Modal Header */}
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <History className="w-6 h-6 text-[#10B981]" />
                  <div>
                    <h2 className="text-2xl font-bold text-white">Versions-Historie</h2>
                    <p className="text-white/60 text-sm">{pages.find(p => p.key === selectedPage)?.label} - {versions.length} Versionen</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowVersions(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-white/60" />
                </button>
              </div>
              
              {/* Versions Liste */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {loadingVersions ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#10B981]/30 border-t-[#10B981]"></div>
                  </div>
                ) : versions.length === 0 ? (
                  <div className="text-center py-20">
                    <p className="text-white/40">Keine Versionen vorhanden</p>
                  </div>
                ) : (
                  versions.map((version, index) => (
                    <div key={version.version_id} className="p-4 bg-white/5 border border-white/10 rounded-lg hover:border-[#10B981]/30 transition-all">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                            index === 0 ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white/60'
                          }`}>
                            v{version.version_number}
                          </div>
                          <div>
                            <p className="text-white font-medium">{version.title}</p>
                            <p className="text-white/50 text-xs">
                              {version.updated_at ? new Date(version.updated_at).toLocaleString('de-DE', { timeZone: 'UTC', hour12: false }) + ' UTC' : 'N/A'}
                            </p>
                          </div>
                        </div>
                        {index === 0 && (
                          <span className="px-2 py-1 bg-[#10B981]/20 text-[#10B981] text-xs rounded">AKTUELL</span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                        <div>
                          <p className="text-white/40">Geändert von</p>
                          <p className="text-white/80 font-mono">
                            {typeof version.updated_by === 'object' ? version.updated_by?.email : version.updated_by || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-white/40">Zeitstempel-Status</p>
                          <p className={`font-medium ${
                            version.timestamp_status === 'pending' ? 'text-yellow-500' :
                            version.timestamp_status === 'confirmed' ? 'text-[#10B981]' :
                            'text-red-500'
                          }`}>
                            {version.timestamp_status === 'pending' ? '⏳ Pending' :
                             version.timestamp_status === 'confirmed' ? '✓ Confirmed' :
                             '❌ Failed'}
                          </p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-white/40 mb-1">Dokumenten-Hash (SHA-256)</p>
                          <p className="text-[#10B981] font-mono text-xs break-all">
                            {version.content_hash}
                          </p>
                        </div>
                      </div>
                      
                      {version.timestamp_proof && (
                        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/10">
                          <button
                            onClick={() => downloadOtsProof(version.timestamp_proof, selectedPage, version.content_hash)}
                            className="px-3 py-1.5 bg-[#10B981] text-black text-xs rounded hover:bg-[#059669] transition-all flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            Download .ots Proof
                          </button>
                          <a
                            href="https://opentimestamps.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded transition-all"
                          >
                            Extern verifizieren →
                          </a>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-white/10 bg-white/5">
                <p className="text-white/60 text-xs">
                  🔒 Append-only Historie - Versionen können niemals gelöscht oder geändert werden
                </p>
              </div>
            </div>
          </div>
        )}
        
      </div>
      
      {/* Custom CSS für Tiptap */}
      <style>{`
        .ProseMirror {
          min-height: 500px;
          padding: 1rem;
          background: white;
          border-radius: 0.5rem;
          color: #000;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p {
          margin: 0.5rem 0;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 1.5rem 0 0.75rem 0;
          color: #10B981;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror li {
          margin: 0.25rem 0;
        }
        .ProseMirror a {
          color: #10B981;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
}
